// Copyright (c) Ping. All rights reserved.
//
// Web host that mounts the chatroom profile POPOVER (`ProfileCardOverlay`,
// Figma 4139:10345) + full MODAL (`ProfileModalOverlay`, Figma 4136:14975) and
// feeds them real peer data. Reads the chat-room shell view-model: the avatar
// tap sets `miniProfileUserId` (popover); the popover's name tap escalates to
// `userProfileUserId` (modal). Profile data comes from
// `GET /v1/users/{id}/profile`; the avatar media id is resolved to a URL via
// the media download endpoint; mutual chats are derived from the shared
// conversation list.

import { useCallback } from 'react';

import {
  type ChatRoomShellViewModel,
  type ProfileActions,
  ProfileCardOverlay,
  ProfileModalOverlay,
  type ProfileMoreMenuProps,
  type ProfileMutualChatView,
  type ProfileView,
  useConversations,
  useSession,
} from '@ping/ui';
import { pingToast } from '@ping/ui';

import { log } from '@core/log/log';
import { addContact } from '@core/network/contacts';
import { getUserProfile } from '@core/network/users';
import { addWebGroupMembers } from '@features/chat/addWebGroupMembers';
import { fetchMediaDownloadUrl } from '@features/chat/mediaUpload';
import { startDirectChatWithAccount } from '@features/chat/startDirectChat';
import { navigateChatRouteId } from '@shell/hosts/chatRouteUrl';

// Persisted peer-profile cache (per-device). Backs the popover's "show cached
// instantly, refresh in the background" behaviour. An in-memory Map fronts
// localStorage so repeat opens in the same session are synchronous; localStorage
// survives reloads. The cached avatar URL may be a stale signed URL — the
// background refresh re-resolves it, so a brief stale avatar is acceptable.
const PROFILE_CACHE_KEY = (userId: string): string => `com.ping.profile.${userId}`;
const profileMemCache = new Map<string, ProfileView>();

function readCachedProfile(userId: string): ProfileView | null {
  const mem = profileMemCache.get(userId);
  if (mem !== undefined) return mem;
  if (typeof localStorage === 'undefined') return null;
  try {
    const raw = localStorage.getItem(PROFILE_CACHE_KEY(userId));
    if (raw === null) return null;
    const v = JSON.parse(raw) as ProfileView;
    if (v !== null && typeof v === 'object' && typeof (v as ProfileView).name === 'string') {
      profileMemCache.set(userId, v);
      return v;
    }
  } catch {
    // Corrupt entry — ignore.
  }
  return null;
}

function writeCachedProfile(userId: string, view: ProfileView): void {
  profileMemCache.set(userId, view);
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.setItem(PROFILE_CACHE_KEY(userId), JSON.stringify(view));
  } catch {
    // Best-effort.
  }
}

export interface ProfileOverlaysHostProps {
  readonly vm: ChatRoomShellViewModel;
}

/** Peer-local clock, e.g. "10:39 AM" — Intl honours the IANA tz id. */
function formatLocalTime(tz: string): string | undefined {
  try {
    return new Intl.DateTimeFormat(undefined, {
      hour: 'numeric',
      minute: '2-digit',
      timeZone: tz,
    }).format(new Date());
  } catch {
    return undefined;
  }
}

/** "Until 13:23" trailing clause for an expiring status. */
function formatUntil(expiresAtMs: number): string | undefined {
  try {
    const t = new Intl.DateTimeFormat(undefined, {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    }).format(new Date(expiresAtMs));
    return `Until ${t}`;
  } catch {
    return undefined;
  }
}

export function ProfileOverlaysHost({ vm }: ProfileOverlaysHostProps): JSX.Element {
  const { conversations } = useConversations();
  const { client } = useSession();

  const load = useCallback(
    async (userId: string): Promise<ProfileView | null> => {
      const p = await getUserProfile(userId);
      if (p === null) return null;

      let avatarUri: string | undefined;
      if (p.avatarMediaId !== undefined) {
        try {
          avatarUri = await fetchMediaDownloadUrl(p.avatarMediaId);
        } catch {
          avatarUri = undefined;
        }
      }

      const localTime = p.timezone !== undefined ? formatLocalTime(p.timezone) : undefined;

      const status =
        p.status !== undefined
          ? {
              ...(p.status.emoji !== undefined ? { emoji: p.status.emoji } : {}),
              text: p.status.text,
              ...(p.status.expiresAt !== undefined
                ? { until: formatUntil(p.status.expiresAt) }
                : {}),
            }
          : undefined;

      const mutualChats: ProfileMutualChatView[] = conversations
        .filter(
          (conv) => conv.kind === 'group' && (conv.memberAccountIds?.includes(userId) ?? false),
        )
        .map((conv) => {
          const count = conv.memberAccountIds?.length ?? 0;
          return {
            id: conv.id,
            name: conv.name ?? 'Group chat',
            ...(count > 0 ? { subtitle: `${count} members` } : {}),
            onPress: () => {
              vm.handlers.closeOverlay('userProfile');
              navigateChatRouteId(conv.id);
            },
          };
        });

      const view: ProfileView = {
        name: p.displayName,
        verified: p.verified,
        ...(p.jobTitle !== undefined ? { jobTitle: p.jobTitle } : {}),
        ...(avatarUri !== undefined ? { avatarUri } : {}),
        ...(localTime !== undefined ? { localTime } : {}),
        ...(status !== undefined ? { status } : {}),
        ...(p.bio !== undefined ? { bio: p.bio } : {}),
        ...(p.companyName !== undefined ? { company: { name: p.companyName } } : {}),
        ...(p.contactEmail !== undefined ? { email: p.contactEmail } : {}),
        ...(p.contactPhone !== undefined ? { phone: p.contactPhone } : {}),
        ...(mutualChats.length > 0 ? { mutualChats } : {}),
      };
      // Persist for instant display on the next open (mutual-chats are derived
      // fresh each time, but caching them is harmless — the refresh overwrites).
      writeCachedProfile(userId, view);
      return view;
    },
    [conversations, vm.handlers],
  );

  // Synchronous cache peek for the popover's instant-then-refresh behaviour.
  const loadCached = useCallback((userId: string): ProfileView | null => {
    return readCachedProfile(userId);
  }, []);

  const buildActions = useCallback(
    (userId: string): ProfileActions => ({
      // Open the direct conversation with this peer — creating it first when one
      // doesn't exist yet. `ChatRouteHost` resolves a route id by `peerAccountId`,
      // so once the row exists we can navigate by the account id.
      onSendMessage: () => {
        vm.handlers.closeOverlay('miniProfile');
        vm.handlers.closeOverlay('userProfile');
        const existing = conversations.find(
          (c) => c.kind === 'direct' && c.peerAccountId === userId,
        );
        if (existing !== undefined) {
          navigateChatRouteId(existing.id);
          return;
        }
        const displayName = readCachedProfile(userId)?.name ?? 'Ping user';
        void startDirectChatWithAccount({ client, accountId: userId, displayName })
          .then((conversationId) => navigateChatRouteId(conversationId))
          .catch((err: unknown) => {
            log.warn({ event: 'profile.sendMessage.failed', message: (err as Error).message });
            pingToast.error({
              title: "Couldn't start chat",
              description: 'Try again in a moment.',
            });
          });
      },
      // Add this person to the viewer's contacts (POST /v1/contacts), with
      // inline toast feedback so the button is functional end-to-end.
      onAddContact: () => {
        void addContact({ userId })
          .then(() => pingToast.success({ title: 'Contact added' }))
          .catch((err: unknown) => {
            log.warn({ event: 'profile.addContact.failed', message: (err as Error).message });
            pingToast.error({ title: "Couldn't add contact", description: 'Try again in a moment.' });
          });
      },
    }),
    [vm.handlers, conversations, client],
  );

  const buildMoreMenu = useCallback(
    (userId: string): ProfileMoreMenuProps => ({
      // The viewer's groups, for "Add to group".
      groups: conversations
        .filter((conv) => conv.kind === 'group')
        .map((conv) => ({ id: conv.id, name: conv.name ?? 'Group chat' })),
      onViewFullProfile: () => vm.handlers.openUserProfile(userId as never),
      onAddToGroup: (groupId: string) => {
        // Actually add this person to the chosen group via the MLS add-member
        // flow (claim device key-packages + one addMembers Commit/Welcome), with
        // toast feedback.
        vm.handlers.closeOverlay('miniProfile');
        vm.handlers.closeOverlay('userProfile');
        const displayName = readCachedProfile(userId)?.name ?? 'Ping user';
        void addWebGroupMembers({
          client: client as unknown as Parameters<typeof addWebGroupMembers>[0]['client'],
          conversationId: groupId,
          members: [{ accountId: userId, displayName }],
        })
          .then(() => pingToast.success({ title: 'Added to group' }))
          .catch((err: unknown) => {
            log.warn({ event: 'profile.addToGroup.failed', message: (err as Error).message });
            pingToast.error({
              title: "Couldn't add to group",
              description: 'Try again in a moment.',
            });
          });
      },
      onBlock: () => {
        log.info({ event: 'profile.block', uidPrefix: userId.slice(0, 6) });
        vm.handlers.closeOverlay('miniProfile');
        vm.handlers.closeOverlay('userProfile');
      },
    }),
    [conversations, vm.handlers, client],
  );

  return (
    <>
      <ProfileCardOverlay vm={vm} load={load} loadCached={loadCached} buildActions={buildActions} buildMoreMenu={buildMoreMenu} />
      <ProfileModalOverlay vm={vm} load={load} buildActions={buildActions} buildMoreMenu={buildMoreMenu} />
    </>
  );
}
